import React from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { linkTabs } from '../../tabHref';
import type { BooksOutletContext } from './booksOutletContext';
import { BOOKS_TABS, resolveBooksTabId } from './booksTabsMeta';
import {
    defaultBooksWindow,
    readRememberedWindow,
    rememberWindow,
} from './booksWindow';
import { windowNote } from './booksWindowMeta';

/** Query keys the date range lives under, shared by all four tabs. */
const FROM_PARAM = 'from';
const TO_PARAM = 'to';

/**
 * Books shell state: which tab the URL selects, and the date range that scopes
 * whichever tab is open.
 *
 * The range lives in the query string rather than component state so it
 * survives a tab switch, a reload and a copied link — every tab under this
 * shell reads the same window, and a shared URL shows the reader what the
 * sender was looking at. Tab navigation carries the current query string across
 * for the same reason.
 */
export const useBooksPage = () => {
    const { pathname } = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const selectedTabId = resolveBooksTabId(pathname);

    // Unset means: whatever the user last chose, else the default for today.
    // A bare /accounting/books is still a complete screen either way.
    //
    // Resolved once per mount rather than on every render — `dayjs()` is not a
    // pure input, and re-reading storage on each render would let a write in one
    // tab yank the window out from under another.
    const fallback = React.useMemo(
        () => readRememberedWindow() ?? defaultBooksWindow(dayjs()),
        [],
    );

    const fromDate = searchParams.get(FROM_PARAM) ?? fallback.from;
    const toDate = searchParams.get(TO_PARAM) ?? fallback.to;

    const onRangeChange = React.useCallback(
        (from: string, to: string) => {
            const next = new URLSearchParams(searchParams);
            next.set(FROM_PARAM, from);
            next.set(TO_PARAM, to);
            // `replace` so tweaking the range doesn't stack history entries —
            // Back should step between tabs, not between date edits.
            setSearchParams(next, { replace: true });
            // Only an explicit change is remembered. A default is not a choice,
            // and storing it would freeze the shell on whatever month it first
            // opened (BOS-166).
            rememberWindow({ from, to });
        },
        [searchParams, setSearchParams],
    );

    // Memoised so the open tab doesn't see a new context object — and refetch —
    // on every unrelated render of the shell.
    const outletContext: BooksOutletContext = React.useMemo(
        () => ({ fromDate, toDate, onRangeChange }),
        [fromDate, toDate, onRangeChange],
    );

    // The shell's range rides along, so moving tabs does not
    // silently reset the window being read.
    const tabs = React.useMemo(
        () => linkTabs(BOOKS_TABS, searchParams),
        [searchParams],
    );

    return {
        tabs,
        selectedTabId,
        note: React.useMemo(
            () => windowNote(selectedTabId, fromDate, toDate),
            [selectedTabId, fromDate, toDate],
        ),
        fromDate,
        toDate,
        onRangeChange,
        outletContext,
    };
};
